import { type QueryClient } from '@tanstack/react-query';
import { invalidateQueries } from '@amzn/gsce-shared-react-lib';

export interface PostMutationRefreshOptions {
  /**
   * Query key collection names to invalidate (e.g. `['audits']` or
   * `['tasks', 'task-detail']`). Passed directly to
   * `invalidateQueries` which matches on the first key segment.
   */
  readonly queryKeys: readonly string[];
  /**
   * Delay in ms before the first invalidation attempt. Gives the
   * backend's ES indexer time to catch up after the DDB write.
   * Default: 3000.
   */
  readonly initialDelayMs?: number;
  /**
   * Delay in ms between subsequent invalidation attempts.
   * Default: 5000.
   */
  readonly intervalMs?: number;
  /**
   * Maximum total time in ms to keep polling before giving up.
   * Prevents infinite polling if the backend is severely degraded.
   * Default: 60000 (1 minute).
   */
  readonly maxDurationMs?: number;
}

const DEFAULT_INITIAL_DELAY_MS = 3_000;
const DEFAULT_INTERVAL_MS = 5_000;
const DEFAULT_MAX_DURATION_MS = 60_000;

/**
 * Registry of active polling schedules keyed by sorted query-key
 * string. When a new schedule is requested for the same keys, the
 * existing one is cancelled and replaced — so rapid mutations
 * (e.g. completing 5 tasks in a row) produce only ONE active poller,
 * not five concurrent ones.
 */
const activeSchedules = new Map<string, () => void>();

/**
 * Derives a stable registry key from the query keys array.
 * Sorted so that `['a', 'b']` and `['b', 'a']` collapse to the same slot.
 */
function deriveScheduleKey(queryKeys: readonly string[]): string {
  return [...queryKeys].sort().join('\0');
}

/**
 * Schedule repeated cache invalidations after a mutation succeeds,
 * polling until either the maximum duration is reached or cancelled.
 *
 * **De-duplication**: If a schedule for the same query keys is already
 * active, it is cancelled and replaced. This prevents N rapid mutations
 * from stacking N concurrent pollers — only the most recent mutation's
 * schedule runs. The replacement resets the clock (fresh `maxDurationMs`
 * window from the latest mutation), ensuring the newest write always
 * gets the full polling window to propagate.
 *
 * Designed to bridge the gap between a DynamoDB write (immediately
 * consistent) and ElasticSearch indexing (eventual). Invalidations
 * trigger React Query to refetch any mounted queries that match the
 * provided keys. The polling continues at a steady cadence until
 * `maxDurationMs` elapses — by then ES will have caught up even
 * in worst-case indexing scenarios.
 *
 * Returns a cleanup function that cancels all future invalidations.
 * Callers in React components can wire this into a useEffect cleanup;
 * callers in mutation callbacks (fire-and-forget) can safely ignore
 * the return value — the polling self-terminates after `maxDurationMs`.
 *
 * This is NOT a React hook — it's a plain function safe to call from
 * `createMutation` callbacks (`onSuccess`, `onSettled`) which run
 * outside of React's hook rules.
 *
 * @example
 *   // Inside a createMutation onSuccess callback:
 *   onSuccess: (queryClient) => {
 *     // Immediate invalidation (existing behavior):
 *     void invalidateQueries(queryClient, [AuditQueryKeys.lists]);
 *     // Poll until ES catches up (up to 60s):
 *     schedulePostMutationRefresh(queryClient, {
 *       queryKeys: [AuditQueryKeys.lists],
 *     });
 *   }
 */
export function schedulePostMutationRefresh(
  queryClient: QueryClient,
  options: PostMutationRefreshOptions,
): () => void {
  const {
    queryKeys,
    initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxDurationMs = DEFAULT_MAX_DURATION_MS,
  } = options;

  // Cancel any existing schedule for the same keys.
  const scheduleKey = deriveScheduleKey(queryKeys);
  const existingCancel = activeSchedules.get(scheduleKey);
  if (existingCancel) {
    existingCancel();
  }

  let cancelled = false;
  let currentTimerId: NodeJS.Timeout | null = null;
  const startTime = Date.now();

  function scheduleNext(delayMs: number): void {
    if (cancelled) return;
    currentTimerId = setTimeout(() => {
      if (cancelled) return;

      const elapsed = Date.now() - startTime;
      if (elapsed >= maxDurationMs) {
        // Final invalidation attempt, then stop and clean up registry.
        void invalidateQueries(queryClient, queryKeys);
        activeSchedules.delete(scheduleKey);
        return;
      }

      void invalidateQueries(queryClient, queryKeys);
      scheduleNext(intervalMs);
    }, delayMs);
  }

  const cancel = (): void => {
    cancelled = true;
    if (currentTimerId !== null) {
      clearTimeout(currentTimerId);
      currentTimerId = null;
    }
    activeSchedules.delete(scheduleKey);
  };

  activeSchedules.set(scheduleKey, cancel);
  scheduleNext(initialDelayMs);

  return cancel;
}
